import { useCallback, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'

/**
 * หัวตารางที่กดเรียงได้ — ใช้ร่วมทุกหน้า คู่กับ backend/src/lib/sorting.js
 *
 * ⚠️ **เรียงที่ server เสมอ** ไม่ใช่เรียง array ในหน้าจอ — ทุกตารางแบ่งหน้าอยู่แล้ว
 *    เรียงฝั่งหน้าจอ = เรียงแค่ 10 แถวที่บังเอิญอยู่หน้านี้ ซึ่งดูเหมือนทำงาน
 *    จนกว่าจะมีหน้าที่สอง แล้วคนจะอ่านผิดว่าแถวแรกของหน้า 1 คืออันดับ 1
 *    (ข้อยกเว้นเดียวคือตารางที่ไม่แบ่งหน้าที่ API — ตอนนี้มี "สถานที่ลงเวลา" ที่เดียว)
 *
 * ── วิธีใช้ของผู้ใช้ปลายทาง ─────────────────────────────────────────────────
 *   คลิก           = เรียงตามคอลัมน์นี้อย่างเดียว (กดซ้ำสลับ ขึ้น ↔ ลง)
 *   Shift + คลิก   = **เพิ่ม** คอลัมน์นี้เข้าไปในการเรียงที่มีอยู่
 *                    กดซ้ำสลับทิศของคอลัมน์นั้นโดยไม่ยุ่งกับคอลัมน์อื่น
 *   คลิกครั้งที่ 3 = เอาคอลัมน์นั้นออกจากการเรียง
 *
 * เรียงหลายคอลัมน์แล้วจะมีเลขลำดับ (1 2 3) กำกับ เพราะถ้ามีแต่ลูกศรอย่างเดียว
 * คนจะไม่รู้ว่าอันไหนถูกใช้ก่อน ซึ่งเปลี่ยนผลลัพธ์ทั้งตาราง
 */

export type SortDir = 'asc' | 'desc'
export type SortKey = { key: string; dir: SortDir }

/** จำนวนคอลัมน์สูงสุดที่เรียงพร้อมกันได้ — ต้องตรงกับ MAX_KEYS ใน backend */
const MAX_KEYS = 4

/** แปลงเป็นค่าที่ส่งขึ้น API: `steps:desc,name:asc` */
export function sortParam(sort: SortKey[]): string {
  return sort.map((s) => `${s.key}:${s.dir}`).join(',')
}

/**
 * state ของการเรียง + ตัวสลับ
 *
 * @param initial ค่าเริ่มต้น ควรตรงกับ fallback ที่ backend ใช้ ไม่งั้นครั้งแรก
 *   ที่เปิดหน้า ลูกศรบนหัวตารางจะไม่ตรงกับลำดับจริงที่ได้มา
 * @param onChange เรียกทุกครั้งที่เรียงเปลี่ยน — ใช้ตีกลับไปหน้า 1
 *   (ไม่กลับหน้า 1 แล้วเรียงใหม่ = ค้างอยู่หน้า 8 ของผลลัพธ์ชุดเก่า)
 */
export function useSort(initial: SortKey[] = [], onChange?: () => void) {
  const [sort, setSort] = useState<SortKey[]>(initial)

  const toggle = useCallback(
    (key: string, additive: boolean) => {
      setSort((prev) => {
        const at = prev.findIndex((s) => s.key === key)

        if (!additive) {
          // เรียงคอลัมน์เดียว: กดคอลัมน์เดิมซ้ำ = สลับทิศ · กดคอลัมน์ใหม่ = เริ่มที่ asc
          if (at === 0 && prev.length === 1) {
            return [{ key, dir: prev[0].dir === 'asc' ? 'desc' : 'asc' }]
          }
          return [{ key, dir: 'asc' }]
        }

        if (at === -1) {
          // เพิ่มคอลัมน์ — เกินเพดานแล้วไม่เพิ่ม (backend ก็ตัดที่ MAX_KEYS เหมือนกัน)
          return prev.length >= MAX_KEYS ? prev : [...prev, { key, dir: 'asc' }]
        }

        const next = [...prev]
        // asc → desc → เอาออก · ครบรอบแล้วคนกดกลับไปสถานะเดิมได้โดยไม่ต้องรีเฟรช
        if (next[at].dir === 'asc') next[at] = { key, dir: 'desc' }
        else next.splice(at, 1)
        return next
      })
      onChange?.()
    },
    [onChange],
  )

  const param = useMemo(() => sortParam(sort), [sort])
  return { sort, setSort, toggle, param }
}

/**
 * เรียง array ในหน้าจอ — **ใช้ได้เฉพาะตารางที่ API ไม่แบ่งหน้า**
 *
 * ⛔ อย่าเอาไปใช้กับตารางที่แบ่งหน้าที่ server เด็ดขาด จะได้แค่เรียง 10 แถวของหน้านี้
 *    ตอนนี้มีที่เดียวที่ใช้ได้คือ "สถานที่ลงเวลา" ซึ่ง `GET /sites` ตั้งใจไม่แบ่งหน้า
 *    (หน้าอื่นเอารายการเดียวกันไปทำ dropdown — ดูข้อยกเว้นใน backend/src/lib/paging.js)
 *
 * เทียบตัวเลขด้วย `-` ส่วนข้อความใช้ localeCompare('th') เพราะเรียงภาษาไทยด้วย
 * `<` `>` จะได้ลำดับตามรหัส Unicode ซึ่งสระลอยขึ้นก่อนพยัญชนะ อ่านแล้วงง
 */
export function sortRows<T>(rows: T[], sort: SortKey[], pick: Record<string, (row: T) => unknown>): T[] {
  if (sort.length === 0) return rows

  return [...rows].sort((a, b) => {
    for (const { key, dir } of sort) {
      const get = pick[key]
      if (!get) continue
      const av = get(a)
      const bv = get(b)
      let cmp = 0

      if (typeof av === 'number' || typeof bv === 'number') cmp = Number(av) - Number(bv)
      else if (typeof av === 'boolean' || typeof bv === 'boolean') cmp = Number(av) - Number(bv)
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'th')

      if (cmp !== 0) return dir === 'asc' ? cmp : -cmp
    }
    return 0
  })
}

function Arrow({ dir }: { dir: SortDir }) {
  return (
    <svg className="sort__arrow" viewBox="0 0 12 12" aria-hidden="true">
      {dir === 'asc' ? <path d="M6 2.5 10 8H2z" /> : <path d="M6 9.5 2 4h8z" />}
    </svg>
  )
}

/**
 * `<th>` ที่กดเรียงได้
 *
 * ใช้ `<button>` ข้างใน ไม่ใช่ onClick บน `<th>` เอง — ต้องโฟกัสด้วยแป้นพิมพ์ได้
 * และ screen reader ต้องอ่านออกว่ากดได้ (`<th onClick>` เฉย ๆ ไม่มีทั้งสองอย่าง)
 */
export function SortTh({
  label,
  sortKey,
  sort,
  onToggle,
  className,
  hint,
}: {
  label: string
  /** คีย์ที่ backend รู้จัก (ต้องอยู่ใน allow ของ endpoint นั้น) */
  sortKey: string
  sort: SortKey[]
  onToggle: (key: string, additive: boolean) => void
  className?: string
  /** คำอธิบายเพิ่มใต้ชื่อคอลัมน์ เช่นหน่วย */
  hint?: string
}) {
  const at = sort.findIndex((s) => s.key === sortKey)
  const active = at !== -1
  const dir = active ? sort[at].dir : undefined

  return (
    <th
      className={[className, 'sort', active ? 'sort--on' : ''].filter(Boolean).join(' ')}
      /* บอก screen reader ว่าตารางถูกเรียงด้วยคอลัมน์ไหนอยู่ — none = กดได้แต่ยังไม่ได้เรียง */
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="sort__btn"
        onClick={(e: MouseEvent) => onToggle(sortKey, e.shiftKey)}
        title={
          active
            ? `เรียง ${dir === 'asc' ? 'น้อย→มาก' : 'มาก→น้อย'} · กดซ้ำเพื่อสลับ · Shift+คลิกเพื่อเรียงหลายคอลัมน์`
            : 'คลิกเพื่อเรียง · Shift+คลิกเพื่อเพิ่มเข้าการเรียงที่มีอยู่'
        }
      >
        <span>{label}</span>
        {active && <Arrow dir={dir as SortDir} />}
        {/* เลขลำดับโผล่เฉพาะตอนเรียงหลายคอลัมน์ — คอลัมน์เดียวไม่ต้องมี "1" ให้รก */}
        {active && sort.length > 1 && <span className="sort__order">{at + 1}</span>}
      </button>
      {hint && <div className="sort__hint">{hint}</div>}
    </th>
  )
}

/**
 * แถบบอกว่ากำลังเรียงด้วยอะไรอยู่ + ปุ่มล้าง
 *
 * มีเฉพาะตอนเรียงหลายคอลัมน์ — คอลัมน์เดียวลูกศรบนหัวตารางก็บอกครบแล้ว
 * แต่พอมีสามคอลัมน์ คนมองหัวตารางอย่างเดียวจะประกอบภาพไม่ออกว่าลำดับเป็นยังไง
 */
export function SortBar({
  sort,
  labels,
  onClear,
}: {
  sort: SortKey[]
  /** คีย์ → ชื่อที่คนอ่านรู้เรื่อง */
  labels: Record<string, string>
  onClear: () => void
}) {
  if (sort.length < 2) return null

  return (
    <p className="sort-bar">
      เรียงตาม:{' '}
      {sort.map((s, i) => (
        <span key={s.key} className="chip">
          {i + 1}. {labels[s.key] ?? s.key} {s.dir === 'asc' ? '↑' : '↓'}
        </span>
      ))}
      <button type="button" className="ghost tiny" onClick={onClear}>
        ล้างการเรียง
      </button>
    </p>
  )
}
